import { useAtom } from 'jotai';
import { FC,useEffect,useState } from 'react';
import * as yup from 'yup';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import { ConfigService } from '../../../services/config/configService';
import { toast } from 'sonner';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { useFormik } from 'formik';
import RequiredInput from '../../../components/forms/InputHOC';
import { Textarea } from 'rizzui';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomSelect from '../../../components/forms/CustomSelect';
import {formsSubmitAtom} from "../../../atom/atom";
import { CONFIG_OPTIONS } from '../../../utils/mapping';



interface ConfigFormProps {
    configSelected?: any;
    formRef?: any;
    setOpenModal: any;
    refetch: () => void;
}

const schema = yup.object({
    label: yup.string().required('El label es requerido'),
    value: yup.string().required('El valor es requerido'),
    description: yup.string().nullable(),
})

const ConfigForm: FC<ConfigFormProps> = ({
    configSelected,
    formRef,
    setOpenModal,
    refetch,
}) => {

    // STATES
    
    const mode = configSelected ? 'Editar' : 'Crear';
    const [selectedView, setSelectedView] = useState<string>('mainInfo');
    const [, setIsSubmitting] = useAtom(formsSubmitAtom);


    // HOOKS

    const { userCan } = usePrivilege();
    const superAdmin = userCan('list', 'company');
    const { getCompaniesList } = useCompanies();
    const service = new ConfigService();
    const { handleErrors } = useHandleErrors();

    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Manejar el envio del formulario de edición
     * @EN Handles the submission of the edit form.
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleUpdate = async (values: any) => {
        try {
            const response = (await service.editConfig(values)).getResponseData();

            if (response.success) {
                toast.success('Configuración editada con éxito');
                refetch();
                setOpenModal(false);
            } else {
                toast.error('Error al editar la configuración');
                return;
            }
        } catch (error: any) {
            toast.error('Error al editar la configuración');
            return;
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Manejar el envio del formulario de creación
     * @EN Handles the submission of the create form.
     * 
     * @param values
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = (await service.createConfig(values)).getResponseData();

            if (response.success) {
                setOpenModal(false);
                refetch();
                toast.success("Configuración creada con éxito");
            } else {
                handleErrors(response);
                return;
            }
        } catch (error: any) {
            handleErrors(error);
            return;
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // FORMIK

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Configuración de Formik para el formulario de configuración
     * @EN Formik configuration for the configuration form.
     */
    // -------------------------------------------------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            label: configSelected?.label ?? '',
            value: configSelected?.value ?? '',
            description: configSelected?.description ?? '',
            company: configSelected?.company?.id ?? ''
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (configSelected) {
                handleUpdate(values);
            } else {
                handleCreate(values);
            }
        },
    });
    // -------------------------------------------------------------------------------------------------------------


    // USE EFFECTS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES Actualizar el estado de isSubmitting cuando cambie el estado de formik.isSubmitting
     * @EN Update the isSubmitting state when formik.isSubmitting changes.
     */
    // -------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting])
    // -------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className='mb grid gap-4 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11 font-medium'>
                    <div className={'md:col-span-12'}>

                        <CustomSelect
                            required
                            isSearchable
                            id={'label'}
                            label="Etiqueta"
                            containerClassName='md:col-span-12'
                            value={CONFIG_OPTIONS.find((g: any) => g.value === formik.values.label)}
                            options={ CONFIG_OPTIONS}
                            onChange={
                                (e: any) => { formik.setFieldValue('label', e?.value) }
                            }
                            error={formik.errors.label}
                        />

                        <RequiredInput 
                            id='value'
                            label='Valor'
                            name='value'
                            formik={formik}
                            error={formik.errors.value}
                        />

                        <Textarea
                            id='description'
                            label='Descripción'
                            rows={4}
                            value={formik.values.description}
                            onChange={(e) => formik.setFieldValue('description', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />

                        {superAdmin && (
                            <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañia"
                                containerClassName='md:col-span-12'
                                value={{ value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label }}
                                options={getCompaniesList()}
                                onChange={
                                    (e: any) => { formik.setFieldValue('company', e?.value) }
                                }
                                error={formik.errors.company}
                            />
                        )}
                    </div>
                </div>
            </form>
        </div>
    )


}

export default ConfigForm;